import React, { useState, useEffect, useRef } from "react";
import "./GroupConfiguration.css";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import Snackbar from "@mui/material/Snackbar";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Loader from "../Components/Loader";

const ProcessCards = ({ isSidebarExpanded }) => {
  const dropdownRef = useRef(null);
  const userId = localStorage.getItem("user_id");
  const roleName = (localStorage.getItem("role_name") || "").toLowerCase();
  const isAdmin = roleName === "admin";
  const [processes, setProcesses] = useState([]);
  const [dropdownId, setDropdownId] = useState(null);
  const [editProcessId, setEditProcessId] = useState(null); // Track which process is in edit mode
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [availableGroups, setAvailableGroups] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const canEditGroupAssignments = isAdmin || userGroups.length > 0;

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownId(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchProcesses = async () => {
    setLoading(true);
    try {
      // Use relative path so axiosInstance.baseURL applies
      const response = await axiosInstance.get(`${API_URL}/api/get/process`);
      setProcesses(response.data?.data || []);
    } catch (err) {
      console.error(
        "Fetch processes failed:",
        err.response?.data || err.message
      );
      // No manual refresh/retry here—interceptor does it.
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  // (`${API_URL}/api/get/group`)

  const fetchGroups = async () => {
    setLoading(true);
    try {
      // Use relative URL so axiosInstance.baseURL is applied
      const response = await axiosInstance.get(`${API_URL}/api/get/group`);
      setAvailableGroups(response.data?.data || []);
    } catch (err) {
      console.error("Fetch groups failed:", err.response?.data || err.message);
      // No manual refresh/retry here—interceptor handles it automatically
    } finally {
      setLoading(false);
    }
  };

const fetchLoggedInUserGroups = async () => {
  if (!userId) return;

  try {
    const res = await axiosInstance.get(
      `${API_URL}/api/get_user_by_id/${userId}`
    );

    const userData = res?.data?.data?.[0];
    const assignedGroups = userData?.assigned_group || [];

    setUserGroups(assignedGroups);
  } catch (err) {
    console.error(
      "Failed to fetch logged-in user groups:",
      err.response?.data || err.message
    );
  }
};

  useEffect(() => {
    fetchProcesses();
    fetchGroups();
    fetchLoggedInUserGroups();
  }, []);

  // Add group to process assigned groups (avoid duplicates)
  const handleAssign = (process_id, groupName) => {
    setProcesses((prev) =>
      prev.map((proc) => {
        if (proc.process_id === process_id) {
          const alreadyAssigned = proc.group.some(
            (g) => g.group_name === groupName
          );
          if (!alreadyAssigned) {
            return {
              ...proc,
              group: [...proc.group, { group_name: groupName }],
            };
          }
        }
        return proc;
      })
    );
    setDropdownId(null);
  };

  // Remove a particular group from assigned groups
  const handleRemoveGroup = (process_id, groupName) => {
    setProcesses((prev) =>
      prev.map((proc) => {
        if (proc.process_id === process_id) {
          return {
            ...proc,
            group: proc.group.filter((g) => g.group_name !== groupName),
          };
        }
        return proc;
      })
    );
  };

  // Toggle edit mode per process
  const toggleEdit = (process_id) => {
    if (!canEditGroupAssignments) return;
    setEditProcessId((prev) => (prev === process_id ? null : process_id));
    setDropdownId(null);
  };

  const handleSave = async (proc, retry = true) => {
    const selectedGroupNames = proc.group.map((g) => g.group_name);

    // Convert selected group names to their corresponding group IDs
    const selectedGroupIds = availableGroups
      .filter((g) => selectedGroupNames.includes(g.group_name))
      .map((g) => g.group_id);

    try {
      const response = await axiosInstance.put(
        `${API_URL}/edit_process/${proc.process_id}`,
        { group_ids: selectedGroupIds }
      );

      if (response.status === 200) {
        console.log("Process updated");
        setSeverity("success");
        setSnackbarMessage("Group Configured SuccessFully");
        setSnackbarOpen(true); // ✅ ADD THIS LINE
        setEditProcessId(null); // Exit edit mode
      } else {
        console.error("Update failed");
        setSeverity("error");
        setSnackbarMessage("Update failed");
        setSnackbarOpen(true);
      }
    } catch (err) {
      const status = err.response?.status;
      console.error(
        "Error updating process:",
        err.response?.data || err.message
      );
    }
  };

  const colorMap = {
    "Flange Blinding": "rgba(0, 123, 255, 0.35)", // blue
    "Flange Deblinding": "rgba(220, 53, 69, 0.35)", // red
    "Flange Testing": "rgba(40, 167, 69, 0.35)", // green
    "Flange Tightening": "rgba(255, 238, 7, 0.35)", // yellow

    // New additions
    "Approval from Client": "rgba(128, 0, 128, 0.35)", // purple
    "Flange Retesting": "rgba(255, 165, 0, 0.35)", // orange
    "Flange Retightening": "rgba(255, 165, 0, 0.35)", // orange
    Retesting: "rgba(255, 157, 0, 0.5)", // orange
  };

  return (
    <div
      className={`table-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
      style={{ minHeight: "85vh" }}
    >
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backdropFilter: "blur(6px)", // blur background
            background: "rgba(0,0,0,0.3)", // optional dark overlay
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999, // stay on top
          }}
        >
          <div style={{ transform: "scale(2)" }}>
            <Loader />
          </div>
        </div>
      )}

      {errorMessage && <p className="error">{errorMessage}</p>}

      {/* <div className="table-header">
      <h1>Group Configuration</h1>
    </div> */}
      <div className="card-container">
        {processes.map((proc) => (
          <div
            key={proc.process_id}
            className="flange-card"
            style={{
              borderLeft: `6px solid ${
                colorMap[proc.process_name] || "#007bff30"
              }`,
            }}
          >
            <div className="group-card-header">
              <h2>{proc.process_name}</h2>
              {canEditGroupAssignments && editProcessId !== proc.process_id && (
                <button
                  className="edit-icon"
                  onClick={() => toggleEdit(proc.process_id)}
                >
                  ✎
                </button>
              )}
            </div>

            <div className="group-list">
              {proc.group.length > 0 ? (
                proc.group.map((g, i) => (
                  <span key={i} className="group-pill">
                    {g.group_name}
                    {editProcessId === proc.process_id && (
                      <button
                        className="remove-btn"
                        onClick={() =>
                          handleRemoveGroup(proc.process_id, g.group_name)
                        }
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))
              ) : (
                <em className="unassigned">Not Assigned</em>
              )}
            </div>

            {editProcessId === proc.process_id && (
              <Dialog
                open={editProcessId === proc.process_id}
                onClose={() => setEditProcessId(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "12px" } }}
              >
                <DialogTitle>Edit Groups for {proc.process_name}</DialogTitle>

                <DialogContent dividers>
                  <div style={{ marginBottom: "1rem" }}>
                    <strong>Assigned Groups:</strong>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginTop: "8px",
                      }}
                    >
                      {proc.group.length > 0 ? (
                        proc.group.map((g, i) => (
                          <span key={i} className="group-pill">
                            {g.group_name}
                            <button
                              className="remove-btn"
                              onClick={() =>
                                handleRemoveGroup(proc.process_id, g.group_name)
                              }
                            >
                              ×
                            </button>
                          </span>
                        ))
                      ) : (
                        <em className="unassigned">Not Assigned</em>
                      )}
                    </div>
                  </div>

                  <div>
                    <strong>Assign New Group:</strong>
                    <div className="dropdown-options">
                      {availableGroups.map((group) => (
                        <div
                          key={group.group_name}
                          className="dropdown-option"
                          onClick={() =>
                            handleAssign(proc.process_id, group.group_name)
                          }
                        >
                          {group.group_name}
                        </div>
                      ))}
                    </div>
                  </div>
                </DialogContent>

                <DialogActions>
                  <Button
                    onClick={() => setEditProcessId(null)}
                    className="btn btn-outline-secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleSave(proc)}
                    variant="contained"
                    color="primary"
                  >
                    Save
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </div>
        ))}
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default ProcessCards;


